// Uitleg per klanttraject-stap in normaal Nederlands: wat doet de stap en wanneer gebeurt het.
// Bedoeld als geheugensteun in de admin (beheer-lijst + per-klant tijdlijn). Eén centrale plek,
// zodat beide schermen dezelfde uitleg tonen.

export interface StepHelp {
  wat: string;
  wanneer: string;
}

export const STEP_HELP: Record<string, StepHelp> = {
  welcome_video: {
    wat: 'Maakt automatisch een opname-taak aan in de opnametool, zodat jij of support een persoonlijke welkomstvideo voor de klant kunt opnemen en inplannen. De opnametool mailt de video daarna zelf naar de klant.',
    wanneer: 'Direct zodra een nieuwe gebruiker een account aanmaakt. De taak verschijnt in de opnametool onder "nog opnemen".',
  },
  onboarding_nudge: {
    wat: 'Een vriendelijk, persoonlijk duwtje naar gebruikers die wel een account hebben maar de onboarding nog niet hebben afgerond. Geen druk, wel laten weten dat support klaarstaat.',
    wanneer: 'Een dagelijkse controle stuurt deze op dag 2 en (als het dan nog stil is) op dag 5 na accountaanmaak. Wordt overgeslagen zodra de gebruiker de onboarding afrondt.',
  },
  celeb_onboarding_done: {
    wat: 'Een felicitatie dat het account klaar is, met de boodschap dat wij intern nog even meekijken en dat support bereikbaar is. Wordt later vervangen door een persoonlijke video.',
    wanneer: 'Op het moment dat de gebruiker de onboarding-wizard afrondt. Eenmalig per gebruiker.',
  },
  brand_setup: {
    wat: 'Een interne taak voor support om handmatig het logo en de huisstijlkleuren van de klant in te stellen. Verschijnt in "Openstaande taken". Zodra support op "Markeer OK" klikt, gaat automatisch de huisstijl-klaar mail naar de klant.',
    wanneer: 'Aan het begin van de klantreis, nadat de gebruiker de onboarding heeft afgerond. Blijft staan tot support de taak afvinkt.',
  },
  brand_ready: {
    wat: 'Een eerlijke mail aan de klant dat de huisstijl is ingericht. Verwijst naar de instellingen als ze iets willen aanpassen. (Geen "automatisch opgehaald"-claim meer.)',
    wanneer: 'Automatisch zodra support de taak "Huisstijl inrichten" op "Markeer OK" zet.',
  },
  welcome_mail: {
    wat: 'De welkomstmail met een korte rondleiding door dibbi, zodat de klant weet waar te beginnen.',
    wanneer: 'Kort na de huisstijl-mail, ongeveer een uur verder in de keten.',
  },
  first_blog_nudge: {
    wat: 'Een nudge om de gebruiker aan te moedigen zijn eerste blog te schrijven. dibbi doet het zware werk, dus dit is een laagdrempelige eerste winst.',
    wanneer: 'Ongeveer een werkdag na de vorige stap. Wordt overgeslagen zodra de gebruiker al een blog publiceerde.',
  },
  connect_social_nudge: {
    wat: 'Een nudge om social media-accounts te koppelen, zodat de gebruiker straks ook kan posten.',
    wanneer: 'Ongeveer een werkdag later. Overgeslagen zodra er een social-account is gekoppeld.',
  },
  first_social_post_nudge: {
    wat: 'Een nudge om de eerste social post te plaatsen.',
    wanneer: 'Ongeveer een werkdag later. Overgeslagen zodra de gebruiker al een post plaatste.',
  },
  import_mailinglist_nudge: {
    wat: 'Een nudge om contacten toe te voegen of een mailinglijst te importeren, zodat de gebruiker zijn blog kan delen.',
    wanneer: 'Ongeveer een werkdag later. Overgeslagen zodra er contacten zijn toegevoegd.',
  },
  first_newsletter_nudge: {
    wat: 'Een nudge om de eerste nieuwsbrief of campagne te versturen. Dit is het hero-moment van de reis: delen met de eigen achterban.',
    wanneer: 'Ongeveer een werkdag later. Overgeslagen zodra de gebruiker al een campagne verstuurde.',
  },
  personal_checkin: {
    wat: 'Een plek voor een persoonlijke check-in: support schrijft en verstuurt zelf een korte, menselijke mail om te vragen hoe het gaat.',
    wanneer: 'Na de nudge-fase. Support kiest zelf het moment en de tekst (verschijnt als taak "Stuur mail").',
  },
  power_signup_form: {
    wat: 'Een power-tip mail over het inschrijfformulier, voor gebruikers die al lekker op gang zijn.',
    wanneer: 'Later in de reis, ongeveer een week na de start.',
  },
  power_widget: {
    wat: 'Een power-tip mail over de widget.',
    wanneer: 'Later in de reis, ongeveer een week na de start.',
  },
  power_wordpress: {
    wat: 'Een power-tip mail over de WordPress-koppeling: blogs verschijnen automatisch op de eigen site via de dibbi-plugin. Overgeslagen zodra WordPress al gekoppeld is.',
    wanneer: 'Laat in de reis, ongeveer 7 werkdagen na de vorige power-tip (widget).',
  },
  celeb_first_blog: {
    wat: 'Een felicitatie met een persoonlijke video zodra de eerste blog live staat, plus een knop om de blog op de eigen website te zetten. Valt terug op een tekst-mail als de video niet lukt.',
    wanneer: 'Op het moment dat de gebruiker zijn eerste blog publiceert. Eenmalig.',
  },
  celeb_first_post: {
    wat: 'Een felicitatie zodra de eerste social post is geplaatst.',
    wanneer: 'Op het moment van de eerste social post. Eenmalig.',
  },
  celeb_first_newsletter: {
    wat: 'Een felicitatie zodra de eerste nieuwsbrief is verstuurd. Dit is het belangrijkste activatie-moment, want hier deelt de klant met zijn achterban.',
    wanneer: 'Op het moment dat de eerste campagne wordt verstuurd. Eenmalig.',
  },
};

export function getStepHelp(stepKey: string, fallbackDescription?: string | null): StepHelp {
  return (
    STEP_HELP[stepKey] ?? {
      wat: fallbackDescription || 'Voor deze stap is nog geen uitleg toegevoegd.',
      wanneer: 'Zie de instellingen van de stap (type en delay) voor de timing.',
    }
  );
}
